import { LayoutDashboard, Users, UsersRound, ArrowLeft, BarChart3, FileText, Bot, SlidersHorizontal, BookOpen, Footprints, ThumbsUp } from "lucide-react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { Button } from "@/components/ui/button"
import { useRole } from "@/hooks/useRole"
import type { Role } from "@/hooks/useRole"

// 管理メニューの出し分け（2026-06-14）:
//   - AIアシスタント設定は認証済み全員（viewer 以上）に表示＝自分の相棒の設定。
//   - その他の管理項目は admin 以上。
//   - ユーザー/ロール一覧は admin も閲覧可（minRole=admin）。ロール編集 UI は画面内で
//     owner 限定・API も owner 強制。
//   - 旧「メンバー」（membership 基準）は Cognito 基準の「ユーザー」へ一本化し削除。
const menuItems = [
  { label: "ダッシュボード", path: "/admin", icon: LayoutDashboard, minRole: "admin" as Role },
  // 旧・独立「設定」ボタンを廃止し、AIアシスタント設定を管理メニューへ統合（2026-06-12）。
  // 2026-06-14: member/viewer も自分の相棒を設定できるよう minRole を viewer（実質全員）に。
  { label: "AIアシスタント設定", path: "/admin/agent", icon: Bot, minRole: "viewer" as Role },
  // これまでの歩み: エージェントの日誌（Daily md）の閲覧。本人の分のみ（2026-07-02 P3）。
  { label: "これまでの歩み", path: "/admin/journey", icon: Footprints, minRole: "viewer" as Role },
  // ナレッジ管理（v2）は /admin/knowledge でフルページとして開く（KnowledgeDialog asPage）。
  // viewer 以上に開放（自分のナレッジを管理できるため）。
  { label: "ナレッジ", path: "/admin/knowledge", icon: BookOpen, minRole: "viewer" as Role },
  { label: "利用状況", path: "/admin/usage", icon: BarChart3, minRole: "admin" as Role },
  // 利用上限の編集は owner 専用（admin には出さない）。ルートも RoleGuard owner、API も owner 強制。
  { label: "利用上限", path: "/admin/usage-limits", icon: SlidersHorizontal, minRole: "owner" as Role },
  { label: "グループ", path: "/admin/groups", icon: UsersRound, minRole: "admin" as Role },
  { label: "監査ログ", path: "/admin/audit", icon: FileText, minRole: "admin" as Role },
  // フィードバック閲覧: 👍👎の一覧（2026-07-02 P3）。
  { label: "フィードバック", path: "/admin/feedback", icon: ThumbsUp, minRole: "admin" as Role },
  { label: "ユーザー", path: "/admin/users", icon: Users, minRole: "admin" as Role },
]

export function AdminSidebar() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { hasMinimumRole, isAdmin } = useRole()
  // 入口は全員に開くが、member/viewer に見えるのは「AIアシスタント設定」だけ。
  // ラベルは admin には「管理」、それ以外には「設定」と出し分ける（実装判断・2026-06-14）。
  const groupLabel = isAdmin ? "管理" : "設定"

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="w-full justify-start gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          チャットに戻る
        </Button>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{groupLabel}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems
                .filter((item) => hasMinimumRole(item.minRole))
                .map((item) => (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                      onClick={() => navigate(item.path)}
                      isActive={pathname === item.path}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  )
}
